import {
  BookingApi as BookingApiGenerated,
  Configuration,
  EmailApi,
  GeoApi,
  OrdersApi,
  PropertyApi,
} from '@/api/generated/fetch-client';
import { responseStatusHandler } from '@/api/middleware';
import { BadRequestErrorPage } from '@/app/pages/errors/bad-request-error-page';
import { ConflictErrorPage } from '@/app/pages/errors/conflict-error-page';
import { ForbiddenErrorPage } from '@/app/pages/errors/forbidden-error-page.tsx';
import { NotFoundErrorPage } from '@/app/pages/errors/not-found-error-page';
import { ServiceUnavailableErrorPage } from '@/app/pages/errors/service-unavailable-error-page';
import { WSManager } from '@/hooks/ws-manager';
import {
  BadRequestError,
  ConflictError,
  ForbiddenError,
  NotFoundError,
  ServiceUnavailableError,
} from '@/types/errors';
import { fetchWithSafeParseJson } from '@/utils/custom-fetch';
import { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  ErrorComponentProps,
  Outlet,
} from '@tanstack/react-router';

import { AccommodationApi } from '@/features/accommodation/api';
import { AccommodationRepository } from '@/features/accommodation/repository/accommodation-repository';
import { AccommodationWSRepository } from '@/features/accommodation/repository/accommodation-ws-repository';
import { BookingApi } from '@/features/booking/api';
import { BookingRepository } from '@/features/booking/repository/booking-repository';
import { OrderApi } from '@/features/order/api';
import { OrderRepository } from '@/features/order/repository/order-repository';
import { CountryApi } from '@/features/personal-details-form/api';
import { EmailRepository } from '@/features/personal-details-form/repository/email/email-repository';
import { GeoRepository } from '@/features/personal-details-form/repository/geo-repository/geo-repository';

import {
  OperationsResponseDto,
  SearchMessage,
} from '@/entities/listing/typings/api-types';

export type RootRouterContext = {
  queryClient: QueryClient;
  configuration: Configuration;
};

export const Route = createRootRouteWithContext<RootRouterContext>()({
  notFoundComponent() {
    return <NotFoundErrorPage />;
  },
  errorComponent: CustomErrorComponent,
  component: Outlet,
  context() {
    const configuration = new Configuration({
      fetchApi: fetchWithSafeParseJson,
      basePath: '/api',
      middleware: [responseStatusHandler],
    });
    return {
      configuration,
      geoRepository: new GeoRepository(new CountryApi(new GeoApi(configuration))),
      orderRepository: new OrderRepository(new OrderApi(new OrdersApi(configuration))),
      accommodationRepository: new AccommodationRepository(
        new AccommodationApi(new PropertyApi(configuration)),
      ),
      accommodationWsRepository: new AccommodationWSRepository(
        new WSManager<OperationsResponseDto, SearchMessage>(
          `${globalThis.location.origin.replaceAll('http', 'ws')}/api/ws/search`,
        ),
      ),
      bookingRepository: new BookingRepository(
        new BookingApi(new BookingApiGenerated(configuration)),
      ),
      emailRepository: new EmailRepository(new EmailApi(configuration)),
    };
  },
});

export function CustomErrorComponent({ error }: ErrorComponentProps) {
  if (error instanceof NotFoundError) {
    return <NotFoundErrorPage />;
  } else if (error instanceof ConflictError) {
    return <ConflictErrorPage />;
  } else if (error instanceof ServiceUnavailableError) {
    return <ServiceUnavailableErrorPage />;
  } else if (error instanceof BadRequestError) {
    return <BadRequestErrorPage />;
  } else if (error instanceof ForbiddenError) {
    return <ForbiddenErrorPage />;
  } else {
    return <ServiceUnavailableErrorPage />;
  }
}
